import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to App
          </Button>
        </Link>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-2">
            🛒 Cart Xray — Privacy Policy / Politique de confidentialité
          </h1>
          <p className="text-muted-foreground mb-8">
            <em>Last updated / Dernière mise à jour : October 2025</em>
          </p>

          <p>
            Cart Xray respects your privacy and complies with the EU General
            Data Protection Regulation (GDPR) and French law (Loi Informatique
            et Libertés).
          </p>

          <p>
            Cart Xray respecte votre vie privée et agit conformément au
            Règlement Général sur la Protection des Données (RGPD) et à la Loi
            Informatique et Libertés.
          </p>

          <hr className="my-8" />

          <h2>1️⃣ Identity of the Data Controller / Responsable du traitement</h2>
          <p>
            <strong>Company / Entreprise :</strong> TS4U SAS
            <br />
            <strong>N° SIRET :</strong> 929 710 952 00011 / RCS Aix-en-Provence
            929 710 952
            <br />
            <strong>N° TVA intracommunautaire :</strong> FR09 929 710 952
            <br />
            <strong>Postal address / Adresse postale :</strong> 105 chemin des
            Valladets, 13510 Éguilles, France
            <br />
            <strong>Contact email :</strong> support.corp@cartxray.com
            <br />
            <strong>Data Protection Officer / DPO :</strong>{" "}
            dpo.corp@cartxray.com
          </p>

          <hr className="my-8" />

          <h2>2️⃣ Data we collect / Données collectées</h2>
          <table className="w-full border-collapse border border-border">
            <thead>
              <tr className="bg-muted">
                <th className="border border-border p-2 text-left">Type</th>
                <th className="border border-border p-2 text-left">
                  Description
                </th>
                <th className="border border-border p-2 text-left">
                  Example / Exemple
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-border p-2">Account data</td>
                <td className="border border-border p-2">
                  Name, email, password (hash), preferences
                </td>
                <td className="border border-border p-2">thomas@example.com</td>
              </tr>
              <tr>
                <td className="border border-border p-2">Receipt images</td>
                <td className="border border-border p-2">
                  Uploaded photos for OCR and analysis
                </td>
                <td className="border border-border p-2">JPEG receipt</td>
              </tr>
              <tr>
                <td className="border border-border p-2">Purchase data</td>
                <td className="border border-border p-2">
                  Date, amount, store, line items
                </td>
                <td className="border border-border p-2">
                  Carrefour – €42.50 – Fruit & Veg
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">Analytics data</td>
                <td className="border border-border p-2">
                  Device type, browser, usage logs
                </td>
                <td className="border border-border p-2">iPhone 14 – Safari</td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  Feedback / corrections
                </td>
                <td className="border border-border p-2">
                  User feedback to improve AI accuracy
                </td>
                <td className="border border-border p-2">
                  "Price misread → €3.50 not €35"
                </td>
              </tr>
            </tbody>
          </table>

          <hr className="my-8" />

          <h2>
            3️⃣ Purposes and Legal Bases / Finalités et bases légales
          </h2>
          <table className="w-full border-collapse border border-border">
            <thead>
              <tr className="bg-muted">
                <th className="border border-border p-2 text-left">
                  Purpose / Finalité
                </th>
                <th className="border border-border p-2 text-left">
                  Legal basis / Base légale
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-border p-2">
                  App functioning and account management
                </td>
                <td className="border border-border p-2">
                  Contract (art. 6 §1 b GDPR)
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  Receipt analysis and categorization (OCR + AI)
                </td>
                <td className="border border-border p-2">
                  Legitimate interest (art. 6 §1 f) + user consent when required
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  Usage analytics and bug monitoring
                </td>
                <td className="border border-border p-2">Legitimate interest</td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  Marketing emails (optional)
                </td>
                <td className="border border-border p-2">
                  Consent (art. 6 §1 a)
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  Model improvement via feedback datasets
                </td>
                <td className="border border-border p-2">
                  Consent + pseudonymisation
                </td>
              </tr>
            </tbody>
          </table>

          <hr className="my-8" />

          <h2>4️⃣ Retention Periods / Durées de conservation</h2>
          <ul>
            <li>Account data : active account + 3 years of inactivity</li>
            <li>Receipts and images : 24 months after upload or until deletion</li>
            <li>Logs and analytics : 13 months</li>
            <li>Legal archives (facturation) : 10 years</li>
          </ul>

          <hr className="my-8" />

          <h2>5️⃣ Recipients & Processors / Destinataires et sous-traitants</h2>
          <p>
            We rely on trusted sub-processors contractually bound under GDPR
            Article 28.
          </p>

          <p>
            Nous faisons appel à des sous-traitants de confiance liés
            contractuellement conformément à l'article 28 du RGPD.
          </p>

          <table className="w-full border-collapse border border-border">
            <thead>
              <tr className="bg-muted">
                <th className="border border-border p-2 text-left">
                  Processor / Sous-traitant
                </th>
                <th className="border border-border p-2 text-left">
                  Role / Rôle
                </th>
                <th className="border border-border p-2 text-left">
                  Data handled / Données traitées
                </th>
                <th className="border border-border p-2 text-left">
                  Location / Lieu d'hébergement
                </th>
                <th className="border border-border p-2 text-left">
                  Safeguards / Garanties
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-border p-2">
                  <strong>Supabase Inc.</strong>
                </td>
                <td className="border border-border p-2">
                  Database & authentication hosting
                </td>
                <td className="border border-border p-2">
                  User accounts, receipts, feedback
                </td>
                <td className="border border-border p-2">🇪🇺 EU (Frankfurt)</td>
                <td className="border border-border p-2">
                  DPA + EU Standard Contractual Clauses (SCCs)
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  <strong>Vercel Inc.</strong>
                </td>
                <td className="border border-border p-2">
                  Front-end hosting + CDN
                </td>
                <td className="border border-border p-2">
                  Minimal logs (IP, headers)
                </td>
                <td className="border border-border p-2">🇪🇺 EU & 🇺🇸 US</td>
                <td className="border border-border p-2">
                  DPA + SCCs + data minimisation
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  <strong>OpenAI LLC</strong>
                </td>
                <td className="border border-border p-2">
                  AI analysis (OCR + categorisation)
                </td>
                <td className="border border-border p-2">
                  Receipt images & text snippets (transiently)
                </td>
                <td className="border border-border p-2">🇺🇸 USA</td>
                <td className="border border-border p-2">
                  DPA + SCCs + no training on user data
                </td>
              </tr>
            </tbody>
          </table>

          <p>
            No data is sold or shared with advertisers.
          </p>

          <p>
            Aucune donnée n'est vendue ni transmise à des tiers à des fins
            commerciales.
          </p>

          <hr className="my-8" />

          <h2>6️⃣ User Rights / Droits des personnes</h2>
          <p>
            Under Articles 15 to 22 GDPR, you may exercise your rights at any
            time :
          </p>
          <ul>
            <li>Access / Accès</li>
            <li>Rectification / Rectification</li>
            <li>Erasure / Effacement</li>
            <li>Restriction / Limitation</li>
            <li>Portability / Portabilité</li>
            <li>Objection / Opposition</li>
            <li>Withdrawal of consent / Retrait du consentement</li>
          </ul>
          <p>
            📩 Contact : dpo.corp@cartxray.com
          </p>

          <p>
            📮 You may also lodge a complaint with the CNIL (
            <a
              href="https://www.cnil.fr"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.cnil.fr
            </a>
            ).
          </p>

          <p>
            📮 Vous pouvez également déposer une réclamation auprès de la CNIL.
          </p>

          <hr className="my-8" />

          <h2>7️⃣ Security Measures / Mesures de sécurité</h2>
          <ul>
            <li>Encryption in transit and at rest (AES-256 / TLS 1.3)</li>
            <li>Access control and least-privilege policies</li>
            <li>Audit logs and anomaly detection</li>
            <li>Regular backups and disaster-recovery tests</li>
          </ul>

          <hr className="my-8" />

          <h2>8️⃣ Transfers outside the EU / Transferts hors UE</h2>
          <p>
            Some sub-processors operate in the United States.
            Transfers are framed by EU Standard Contractual Clauses and
            supplementary safeguards to ensure an adequate level of protection.
          </p>

          <p>
            Certains sous-traitants opèrent aux États-Unis ; les transferts sont
            encadrés par les clauses contractuelles types et des garanties
            complémentaires.
          </p>

          <hr className="my-8" />

          <h2>9️⃣ Cookies & Analytics / Traceurs et mesure d'audience</h2>
          <p>
            Cart Xray uses only functional and audience-measurement cookies.
            Consent is requested for any non-essential cookies (marketing or A/B
            testing).
          </p>

          <p>
            Cart Xray n'utilise que des cookies fonctionnels et de mesure
            d'audience. Votre consentement est recueilli pour tout autre cookie
            non essentiel.
          </p>

          <hr className="my-8" />

          <h2>🔟 Contact & Amendments / Contact et modifications</h2>
          <p>
            Questions or requests → dpo.corp@cartxray.com
          </p>

          <p>
            We may update this policy from time to time; the latest version is
            always available on our website.
          </p>

          <p>
            Nous pouvons modifier la présente politique à tout moment ; la
            version à jour est publiée sur notre site.
          </p>

          <hr className="my-8" />

          <p className="text-center font-semibold">
            TS4U SAS – Cart Xray © 2025
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
